import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatDkk } from "@/lib/api";
import { CalendarDays, CheckCircle2, Clock3, Star, X } from "lucide-react";
import ProfileAvatar from "@/components/profile/ProfileAvatar";
import { customerRulesPath, useBusinessRules } from "@/lib/businessRules";

const minutesLabel = (min, max) => {
  if (!min && !max) return "tid aftales";
  return min === max ? `${min} min` : `${min}–${max} min`;
};

const durationQuantity = (minutes) => {
  const value = Number(minutes) || 0;
  if (value < 60) return `${value} min.`;
  const hours = value / 60;
  const quantity = Number.isInteger(hours) ? String(hours) : String(hours).replace(".", ",");
  return `${quantity} ${hours === 1 ? "time" : "timer"}`;
};

const proposalTotal = (proposal) => proposal.price_model === "hourly"
  ? proposal.estimated_price_max ?? Math.round((proposal.hourly_rate || 0) * (proposal.estimated_minutes_max || 0) / 60)
  : proposal.fixed_price;

const formatAppointment = (value) => value
  ? new Intl.DateTimeFormat("da-DK", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date(value))
  : "Tidspunkt afventer";

const proposalPrice = (proposal) => `Samlet pris: ${formatDkk(proposalTotal(proposal))}`;

function ExpertProposalCard({ proposal, onSelect }) {
  const accepted = proposal.status === "accepted";
  const notSelected = proposal.status === "not_selected";

  return (
    <article className={`flex min-h-[300px] flex-col rounded-xl border bg-white p-4 ${
      accepted ? "border-brand-fresh ring-1 ring-brand-fresh" : "border-brand-line"
    }`} data-testid={`proposal-${proposal.id}`}>
      <div className="flex items-start gap-3">
        <ProfileAvatar photoUrl={proposal.expert_photo} name={proposal.expert_name} size="md" className="!h-11 !w-11"/>
        <div className="min-w-0 flex-1">
          {accepted && (
            <div className="mb-1 inline-flex items-center gap-1 rounded-md bg-[#E6F4EE] px-2 py-0.5 text-[10px] font-medium text-brand-fresh">
              Valgt ekspert <CheckCircle2 size={11}/>
            </div>
          )}
          <h3 className="truncate text-[15px] font-semibold text-brand-ink">{proposal.expert_name}</h3>
          <div className="truncate text-[11px] text-brand-muted">
            {proposal.expert_headline || "Ekspert"}
            {proposal.expert_years > 0 ? ` · ${proposal.expert_years} års erfaring` : ""}
          </div>
          {proposal.expert_public_reputation?.source === "completed_paid_tasks" && (proposal.expert_rating_count || 0) > 0 && (
            <div className="mt-1 flex items-center gap-1 text-[11px] text-brand-body">
              <Star size={11} className="fill-[#D97706] text-[#D97706]"/> {proposal.expert_rating_avg?.toFixed(1)}
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 overflow-hidden rounded-lg border border-brand-line">
        <div className="bg-brand-wash px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-brand-muted">Tilbud</div>
        <div className="flex items-start justify-between gap-3 px-3 py-2.5">
          <div className="min-w-0"><div className="truncate text-xs font-medium text-brand-ink">{proposal.service_title || "Løsning af opgave"}</div><div className="mt-0.5 text-[11px] text-brand-muted">Forventet {minutesLabel(proposal.estimated_minutes_min, proposal.estimated_minutes_max)}</div></div>
          <div className="shrink-0 text-right"><div className="font-display text-lg font-semibold text-brand-ink">{formatDkk(proposalTotal(proposal))}</div>{proposal.price_model === "hourly" && <div className="text-[10px] text-brand-muted">{durationQuantity(proposal.estimated_minutes_max)} × {formatDkk(proposal.hourly_rate)}/t</div>}</div>
        </div>
      </div>

      <div className="mt-5">
        <div className="text-[10px] text-brand-muted">Kan hjælpe</div>
        {proposal.availability_type === "asap" ? (
          <div className="mt-1 flex items-center gap-1.5 text-[12px] font-medium text-brand-green">
            <Clock3 size={13}/> Så hurtigt som muligt
          </div>
        ) : (
          <div className="mt-1 space-y-1.5">
            {(proposal.proposed_times || []).slice(0, 3).map((time) => (
              <div key={time} className="flex items-center gap-1.5 text-[12px] text-brand-body">
                <CalendarDays size={13} className="text-brand-green"/> {formatAppointment(time)}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-5 flex-1">
        <div className="text-[10px] text-brand-muted">Omfang</div>
        <p className="mt-1 line-clamp-3 text-[12px] leading-5 text-brand-body whitespace-pre-wrap">{proposal.scope}</p>
      </div>

      <div className="mt-5">
        {accepted ? (
          <div className="flex min-h-10 items-center justify-center rounded-lg bg-brand-fresh px-3 text-sm font-semibold text-white">Valgt</div>
        ) : notSelected ? (
          <div className="flex min-h-10 items-center justify-center rounded-lg bg-brand-soft px-3 text-sm text-brand-muted">Ikke valgt</div>
        ) : (
          <button onClick={() => onSelect(proposal)} className="min-h-10 w-full rounded-lg border border-brand-line text-sm font-semibold text-brand-green hover:border-brand-green hover:bg-brand-soft" data-testid={`select-proposal-${proposal.id}`}>
            Vælg {proposal.expert_name}
          </button>
        )}
      </div>
    </article>
  );
}

export default function ProblemProposals({ problemId, onSelected, onRequestOtherTime }) {
  const { rules, error: rulesError } = useBusinessRules();
  const manualPayment = rules?.request_payment_method === "manual_expert";
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [selectedProposal, setSelectedProposal] = useState(null);

  const reload = () => {
    api.get(`/problems/${problemId}/proposals`).then((response) => setItems(response.data)).catch(() => {});
  };

  useEffect(() => {
    reload();
    const timer = setInterval(reload, 6000);
    return () => clearInterval(timer);
  }, [problemId]);

  const openConfirmation = (proposal) => {
    setSelectedProposal(proposal);
  };

  const closeConfirmation = () => {
    if (busyId) return;
    setSelectedProposal(null);
  };

  const confirmChoice = async () => {
    if (!selectedProposal) return;
    setBusyId(selectedProposal.id);
    try {
      const { data } = await api.post(`/proposals/${selectedProposal.id}/select`, {
        selected_time: null,
      });
      setSelectedProposal(null);
      reload();
      onSelected?.(data);
    } catch (error) {
      alert(error.response?.data?.detail || "Kunne ikke vælge eksperten");
    } finally {
      setBusyId(null);
    }
  };

  const requestOtherTime = () => {
    const proposal = selectedProposal;
    setSelectedProposal(null);
    onRequestOtherTime?.(proposal);
  };

  if (items.length === 0) {
    return <div className="rounded-xl border border-dashed border-brand-line px-4 py-6 text-center text-sm text-brand-muted">Ingen prisforslag endnu.</div>;
  }

  return (
    <div data-testid={`proposals-for-${problemId}`}>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {items.map((proposal) => (
          <ExpertProposalCard key={proposal.id} proposal={proposal} onSelect={openConfirmation}/>
        ))}
      </div>

      {selectedProposal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/45 p-4" onClick={closeConfirmation}>
          <div className="w-full max-w-lg overflow-y-auto rounded-2xl border border-brand-line bg-brand-wash p-5 shadow-xl max-h-[92vh]" role="dialog" aria-modal="true" aria-labelledby="select-expert-title" onClick={(event) => event.stopPropagation()} data-testid="select-expert-confirmation">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-brand-muted">Bekræft valg</div>
                <h3 id="select-expert-title" className="mt-1 font-display text-xl font-semibold text-brand-ink">Vil du vælge {selectedProposal.expert_name}?</h3>
              </div>
              <button type="button" onClick={closeConfirmation} aria-label="Luk" className="rounded-full p-1 hover:bg-brand-soft"><X size={18}/></button>
            </div>

            <div className="mt-4 rounded-xl bg-brand-soft p-3 text-sm">
              <div className="font-medium text-brand-ink">{proposalPrice(selectedProposal)}</div>
              <div className="mt-1 text-brand-body">{selectedProposal.service_title || "Løsning af opgave"} · forventet {minutesLabel(selectedProposal.estimated_minutes_min, selectedProposal.estimated_minutes_max)}</div>
              {selectedProposal.price_model === "hourly" && <div className="text-brand-body">{durationQuantity(selectedProposal.estimated_minutes_max)} × {formatDkk(selectedProposal.hourly_rate)}/time</div>}
              <div className="mt-2 text-[10px] uppercase tracking-widest text-brand-muted">Omfang</div>
              <div className="text-brand-ink whitespace-pre-wrap">{selectedProposal.scope}</div>
            </div>

            <div className="mt-4 rounded-xl border border-brand-line-strong bg-[#EFF7F1] p-3 text-sm text-brand-green">
              {manualPayment ? "Du betaler direkte til den valgte ekspert. Eksperten tjekker og bekræfter betalingen. SpørgMig opbevarer ikke pengene; eventuelle tilbagebetalinger håndteres direkte med eksperten." : "Når du har valgt eksperten, er næste trin betaling til SpørgMig. Derefter aftaler I et konkret tidspunkt på opgaven."}
            </div>

            <p className="mt-4 text-sm text-brand-body">
              Når du bekræfter, bliver {selectedProposal.expert_name} valgt og de øvrige prisforslag lukkes. Der trækkes ikke penge, når du bekræfter valget.
            </p>
            <button type="button" onClick={requestOtherTime} className="mt-3 text-sm font-medium text-brand-green hover:underline" data-testid="request-other-time">Foreslå andet tidspunkt</button>

            <p className="mt-4 text-xs leading-5 text-brand-muted" data-testid="customer-booking-rules-acknowledgement">
              Ved at vælge eksperten og fortsætte til betaling accepterer du reglerne for betaling, afbud og mødetid. <Link to={customerRulesPath} target="_blank" className="font-semibold text-brand-green underline">Se regler</Link>
            </p>

            {rulesError && <p role="alert" className="mt-3 text-sm text-red-700">{rulesError}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={closeConfirmation} disabled={!!busyId} className="btn-secondary">Annuller</button>
              <button type="button" onClick={confirmChoice} disabled={!!busyId || !rules} className="btn-primary disabled:opacity-50" data-testid="confirm-select-expert">
                {busyId ? "Bekræfter…" : "Bekræft valg"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

